<template>
  <div class="flex flex-col gap-2">
    <!-- AVG. Total Cost per Ton (YTD) -->
    <div class="h-full w-full p-5 flex flex-col gap-3 bg-white rounded-lg shadow-md">
      <div class="pb-3 flex flex-row items-center justify-between border-b-2 border-dashed">
        <span class="text-xl font-semibold">
          AVG. Total Cost per Ton (YTD)
        </span>
        <div class="flex flex-row items-center justify-between gap-2">
          <select-with-search
            v-model="localFilters.avg_total_cost_site"
            class="w-64"
            placeholder="Select Site"
            :options="siteOptions"
            :bottom="true"
          />
          <select-with-search
            v-model="localFilters.avg_total_cost_third_party_name"
            class="w-64"
            placeholder="Select 3rd party"
            :options="ownersOptions"
            :bottom="true"
          />
        </div>
      </div>
      <vs-row
        vs-w="12"
        class="items-stretch"
      >
        <vs-col
          vs-w="2"
          class="flex flex-col border rounded-lg pr-4 pt-4 pb-0 px-4"
        >
          <span class="font-semibold text-xl mb-0">{{ averageCostData.total_year ? averageCostData.total_year.label : '0' }}</span>
          <div class="flex-1 flex flex-col justify-center items-center">
            <span class="font-bold text-5xl leading-none mb-2">{{ averageCostData.total_year ? averageCostData.total_year.avg_total_cost_per_ton == null ? '0.00' : averageCostData.total_year.avg_total_cost_per_ton.toFixed(2) : '0.00' }}</span>
            <span class="text-2xl font-normal text-gray-700 -mt-2">{{ averageCostData.total_year ? averageCostData.total_year.unit.replace('$', 'USD') : 'USD' }}</span>
          </div>
        </vs-col>
        <vs-col
          vs-w="10"
          class="flex flex-col border rounded-lg p-4 relative"
          v-if="chartData.datasets[0].data.length > 0"
        >
          <bar-chart
            :data="chartData"
            :options="chartOptions"
            :plugins="chartPlugins"
          />
        </vs-col>
        <vs-col
          vs-w="10"
          class="h-56 flex items-center justify-center"
          v-else
        >
          <span class="text-grey-001"> No data found </span>
        </vs-col>
      </vs-row>
    </div>
    <!-- 3rd Party Coal Logistic Cost -->
    <div class="h-full w-full p-5 flex flex-col gap-3 bg-white rounded-lg shadow-md">
      <div class="pb-3 flex flex-row items-center justify-between border-b-2 border-dashed">
        <span class="text-xl font-semibold">
          3rd Party Coal Logistic Cost
        </span>
        <div class="flex flex-row items-center justify-between gap-2">
          <select-with-search
            v-model="localFilters.third_party_logistic_cost_site"
            class="w-64"
            placeholder="Select Site"
            :options="siteOptions"
            :bottom="true"
          />
          <select-with-search
            v-model="localFilters.third_party_logistic_cost_third_party_name"
            class="w-64"
            placeholder="Select 3rd party"
            :options="ownersOptions"
            :bottom="true"
          />
        </div>
      </div>
      <div class="w-full flex flex-row items-center">
        <div class="w-1/3 h-full">
          <bar-chart
            class="w-full h-full"
            :data="barChartDataCost"
            :options="horizontalBargeChartOptions"
            :plugins="horizontalBarChartPlugins"
          />
        </div>
        <div class="w-2/3 flex flex-col gap-3">
          <div
            v-for="(chartDataCost, index) of lineChartData"
            :key="index"
            class="h-24"
          >
            <line-chart
              class="h-full"
              :data="chartDataCost"
              :options="{
                ...lineChartOptions,
                ...((index === lineChartData.length - 1) && lineChartTicksOption)
              }"
              :plugins="index === lineChartData.length - 1 ? [roundedTickPlugin()] : []"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- Sets -->
    <div class="h-full w-full p-5 flex flex-col gap-3 bg-white rounded-lg shadow-md">
      <div class="pb-3 flex flex-row items-center justify-between">
        <span class="text-xl font-semibold">
          Sets
        </span>
      </div>
      <div
        v-if="barChartDataSets.datasets[0].data.length > 0"
        class="w-full h-full"
      >
        <bar-chart
          class="w-full h-full"
          :data="barChartDataSets"
          :options="barChartOptionsSets"
          :plugins="[
            ...chartPlugins,
            drawBarValuePlugin()
          ]"
          :height="500"
        />
      </div>
      <div
        v-else
        class="w-full h-56 flex items-center justify-center"
      >
        <span class="text-grey-001"> No data found </span>
      </div>
    </div>
  </div>
</template>

<script>
import {isNumber, slice} from "lodash";
import { mapGetters } from 'vuex';

import BarChart from '@/components/barChart';
import LineChart from '@/components/lineChart';
import SelectWithSearch from '@/components/selectWithSearch';

export default {
  name: 'LogisticCost3rdPartyCoal',
  components: {
    SelectWithSearch,
    BarChart,
    LineChart
  },
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  watch: {
    filters: {
      handler() {
        this.fetchLogisticCost3rdPartyCoal()
      },
      deep: true
    },
    localFilters: {
      handler() {
        this.fetchLogisticCost3rdPartyCoal()
      },
      deep: true
    },
    companies: {
      deep: true,
      immediate: true,
      handler(val) {
        if(val) {
          this.siteOptions = val.map(item => ({
            text: item.code,
            value: item.code
          }))
        }
      }
    },
    owners: {
      handler(newOwners) {
        if (newOwners) {
          this.ownersOptions = newOwners.map(owner => ({
            text: owner.owner_name,
            value: owner.owner_id
          }));
        }
      },
      immediate: true
    }
  },
  data() {
    return {
      localFilters: {
        avg_total_cost_site: '',
        avg_total_cost_third_party_name: '',
        third_party_logistic_cost_site: null,
        third_party_logistic_cost_third_party_name: null
      },
      siteOptions: [],
      thirdPartyOptions: [],
      ownersOptions: []
    }
  },
  computed: {
    ...mapGetters({
      logisticCost3rdPartyCoal: 'CostAnalysis/logisticCost3rdPartyCoal',
      companies: 'Companies/companies',
      owners: 'TugManagement/owners'
    }),
    year() {
      return this.filters?.year || new Date().getFullYear()
    },
    averageCostData() {
      return this.logisticCost3rdPartyCoal?.average_cost || {}
    },
    thirdPartyCostData() {
      return this.logisticCost3rdPartyCoal?.third_party_coal_logistic_cost || {}
    },
    setsData() {
      return this.logisticCost3rdPartyCoal.sets ? this.logisticCost3rdPartyCoal.sets : []
    },
    chartData() {
      const monthlyData = this.averageCostData.grouped_by_month || []
      const labels = monthlyData.map(item => item.label)
      const tonnageData = monthlyData.map(item => item.tonnage)
      const costData = monthlyData.map(item => item.avg_total_cost_per_ton)

      return {
        labels: labels.length > 0 ? labels : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: [
          {
            type: 'bar',
            label: 'Ton',
            data: tonnageData.length > 0 ? tonnageData : [],
            backgroundColor: '#4fd1c5',
            borderRadius: 8,
            yAxisID: 'y',
            barPercentage: 0.7,
            categoryPercentage: 0.7,
            order: 2
          },
          {
            type: 'line',
            label: 'USD/Ton',
            data: costData.length > 0 ? costData : [],
            borderColor: '#1e3a8a',
            backgroundColor: '#1e3a8a',
            fill: false,
            tension: 0.4,
            pointRadius: 5,
            pointBackgroundColor: '#1e3a8a',
            yAxisID: 'y1',
            order: 1
          }
        ]
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: true,
        layout: {
          padding: {
            left: 25,
            top: 30,
            right: 25
          }
        },
        scales: {
          y: {
            type: 'linear',
            grid: { display: false },
            beginAtZero: true,
            ticks: {
              color: 'black',
              font: { family: 'Poppins' },
              callback: function(value, index, ticks) {
                if (index === ticks.length - 1) {
                  return '\n';
                }
                return value;
              }
            }
          },
          x: {
            offset: true,
            grid: { display: false },
            title: { display: false },
            ticks: { font: { family: 'Poppins', weight: 800 } }
          },
          y1: {
            display: false,
            type: 'linear',
            position: 'right',
            title: { display: false },
            grid: { display: false },
            beginAtZero: true
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              font: { family: 'Poppins', weight: 'semibold' }
            }
          },
          axisIcon: true,
          chartAreaBorder: {
            borderColor: 'black',
            borderWidth: 2
          },
          customTitle: {
            y: {
              display: true,
              text: 'Ton',
              color: '#000000',
              font: {
                family: 'Poppins',
                size: 16,
                weight: 600
              }
            }
          },
          tooltip: {
            enabled: true,
            callbacks: {
              title: ctx => ctx[0].label,
              label: ctx => {
                if (ctx.dataset.label === 'Ton') {
                  return `${ctx.parsed.y.toLocaleString()} Ton`;
                } else {
                  return `${ctx.parsed.y.toFixed(2)} USD/Ton`;
                }
              }
            }
          }
        }
      };
    },
    chartPlugins() {
      return [
        this.chartAreaBorderPlugin(),
        this.customTitlePlugin(),
        this.axisIconPlugin()
      ]
    },
    barChartDataCost() {
      const summaryData = this.thirdPartyCostData.summary?.data || []
      const labels = summaryData.map(item => `${item.label} (${item.unit.replace('$', 'USD')})`)
      const values = summaryData.map(item => item.value || 0)

      return {
        labels: labels,
        datasets: [
          {
            data: values.map((value, index) => ({ x: labels[index], y: 10 })),
            backgroundColor: [
              '#8850E9', // Cargo (Ton)
              '#4fd1c5', // Paid (Ton)
              '#B05A7A', // Set (Trips)
              '#F7D154', // Cost (USD)
              '#F7A154' // Unit Cost (USD/Ton)
            ],
            borderRadius: 12
          }
        ]
      }
    },
    horizontalBargeChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        categoryPercentage: 0.4,
        barPercentage: 1,
        layout: {
          padding: {
            right: 30
          }
        },
        indexAxis: 'y',
        parsing: {
          xAxisKey: 'y',
          yAxisKey: 'x'
        },
        scales: {
          x: {
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              display: false,
              stepSize: 1
            },
            title: {
              display: true,
              align: 'center',
              text: this.thirdPartyCostData.summary?.year || new Date().getFullYear(),
              font: { size: 12, family: 'Poppins' },
              color: 'rgba(0, 0, 0, 1)'
            }
          },
          y: {
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              display: false
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          chartAreaBorder: {
            borderColor: 'black',
            borderWidth: 2
          },
          tooltip: {
            enabled: false,
            backgroundColor: '#FAFDFF',
            titleColor: '#000000',
            titleFont: {
              weight: 'Bold',
              family: 'Poppins'
            },
            titleAlign: 'center',
            bodyColor: '#000000',
            bodyFont: {
              family: 'Poppins'
            },
            bodyAlign: 'center',
            borderWidth: 1,
            cornerRadius: 6,
            displayColors: false
          }
        }
      }
    },
    horizontalBarChartPlugins() {
      return [
        this.baselinePlugin(),
        this.chartAreaBorderPlugin(),
        this.drawBarLabelPlugin()
      ];
    },
    lineChartData() {
      const summaryData = this.thirdPartyCostData.summary?.data || []
      
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      
      const datasets = summaryData.map((item) => {
        const monthlyData = item.grouped_by_month || []
        const data = Array.from({ length: 12 }, (_, i) => {
          const monthIndex = i + 1
          const monthData = monthlyData.find(month => month.month === monthIndex.toString() || month.label === months[i] || month.month === (i + 1).toString())
          return monthData?.value || 0
        })
        
        return {
          label: item.label,
          data: data,
          borderColor: '#000',
          backgroundColor: '#000',
          pointRadius: 4,
          borderWidth: 2,
          unit: item.unit.replace('$', 'USD')
        }
      })

      return datasets.map((item) => ({
        labels: months,
        datasets: [item]
      }))
    },
    lineChartOptions() {
      return {
        responsive: true,
        stacked: false,
        maintainAspectRatio: false,
        scales: {
          x: {
            display: true,
            offset: true,
            ticks: {
              display: true,
              font: { size: 12, family: 'Poppins' },
              color: 'rgba(0, 0, 0, 1)',
              padding: 20,
              callback: function(value, index, ticks) {
                return index === ticks.length - 0 ? this.getLabelForValue(value) : '';
              }
            },
            grid: {
              display: false,
              drawOnChartArea: false,
              drawBorder: false,
              drawTicks: false
            }
          },
          y: {
            type: 'linear',
            display: false,
            offset: true,
            grid: {
              drawOnChartArea: false
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            backgroundColor: '#FAFDFF',
            titleColor: '#000000',
            titleFont: {
              weight: 'Bold',
              family: 'Poppins'
            },
            titleAlign: 'center',
            bodyColor: '#000000',
            bodyFont: {
              family: 'Poppins'
            },
            bodyAlign: 'center',
            borderWidth: 1,
            cornerRadius: 6,
            displayColors: false,
            callbacks: {
              title(tooltipItem) {
                return `${tooltipItem[0].label} (Actual)`;
              },
              label(tooltipItem) {
                const value = tooltipItem.parsed.y;
                const unit = tooltipItem.dataset.unit || '';
                return `${value.toLocaleString()} ${unit.replace('$', 'USD')}`;
              }
            }
          }
        }
      }
    },
    lineChartTicksOption() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            bottom: 5
          }
        },
        scales: {
          x: {
            ticks: {
              display: true,
              font: { size: 12, family: 'Poppins' },
              color: 'rgba(0, 0, 0, 1)',
              padding: 20
            },
            grid: {
              display: true,
              drawOnChartArea: false,
              drawBorder: false,
              drawTicks: true
            },
            offset: true
          },
          y: {
            display: false,
            ticks: {
              display: false
            },
            grid: {
              display: false
            },
            offset: true
          }
        }
      }
    },
    barChartDataSets() {
      const labels = this.setsData.map(item => item.label || item.route)
      const trips = this.setsData.map(item => item.trips || 0)

      return {
        labels: labels.length > 0 ? labels : [],
        datasets: [
          {
            label: 'Trips',
            data: trips.length > 0 ? trips : [],
            backgroundColor: '#223C6A',
            borderRadius: 8,
            barPercentage: 0.6,
            categoryPercentage: 0.6
          }
        ]
      }
    },
    barChartOptionsSets() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            right: 30,
            top: 30,
            left: 20
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            callbacks: {
              label: ctx => `${ctx.parsed.y} trips`
            }
          },
          datalabels: {
            anchor: 'end',
            align: 'end',
            color: '#222',
            font: { weight: 'bold', size: 16 },
            formatter: value => value
          },
          customTitle: {
            y: {
              display: true,
              text: 'Trips',
              color: '#000000',
              font: { weight: 'normal', size: 14, family: 'Poppins' }
            }
          }
        },
        title: {
          display: true,
          text: 'Trips',
          font: { weight: 'bold', size: 16, family: 'Poppins' }
        },
        scales: {
          x: {
            grid: { display: false, drawBorder: false },
            ticks: {
              font: { size: 14, family: 'Poppins' },
              color: '#222'
            }
          },
          y: {
            grid: { display: false, drawBorder: false },
            beginAtZero: true,
            offset: false,
            title: {
              display: false
            },
            ticks: {
              font: { size: 14, family: 'Poppins' },
              color: '#222',
              callback: function(value, index, ticks) {
                if (index === ticks.length - 1) {
                  return '\n';
                }
                return value;
              }
            }
          }
        }
      }
    }
  },
  mounted() {
    this.fetchLogisticCost3rdPartyCoal()
    this.fetchCompanies()
    this.fetchOwners()
  },
  methods: {
    async fetchLogisticCost3rdPartyCoal() {
      const apiFilters = {
        year: this.filters?.year || null,
        month: this.filters?.month || null,
        owner_id: this.filters?.owner_id || null,
        route_name: this.filters?.route_name || null,
        avg_total_cost_site: this.localFilters.avg_total_cost_site || null,
        avg_total_cost_third_party_name: this.localFilters.avg_total_cost_third_party_name || null,
        third_party_logistic_cost_site: this.localFilters.third_party_logistic_cost_site || null,
        third_party_logistic_cost_third_party_name: this.localFilters.third_party_logistic_cost_third_party_name || null
      }
      try {
        await this.$store.dispatch('CostAnalysis/fetchLogisticCost3rdPartyCoal', apiFilters)
      } catch (err) {
        this.showFailedNotification(this, err?.message || 'Failed to fetch logistic cost 3rd party coal')
      }
      
    },
    async fetchCompanies() {
      try {
        await this.$store.dispatch('Companies/fetchCompanies')
      } catch (error) {
        this.showFailedNotification(this, 'Failed to fetch Companies, please try again')
      }
    },
    async fetchOwners() {
      try {
        await this.$store.dispatch('TugManagement/fetchOwners')
      } catch (error) {
        this.showFailedNotification(this, 'Failed to fetch Owners, please try again')
      }
    },
    formatNumber(value) {
      if (typeof value === 'number') {
        return value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      }
      return '0.00'
    },
    axisIconPlugin() {
      return {
        id: 'axisIcons',
        beforeDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, right, bottom }, scales } = chart;

          const topIcon = {
            text: '\u2BC5',
            x: left,
            y: top - 15,
            color: '#000',
            font: 'bold 20px FontAwesome'
          };

          const rightIcon = {
            text: '\u2BC8',
            x: right + 15,
            y: bottom + 2,
            color: '#000',
            font: 'bold 20px Poppins'
          }

          ctx.save()
          ctx.font = topIcon.font
          ctx.fillStyle = topIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(topIcon.text, topIcon.x, topIcon.y)
          ctx.restore();

          ctx.save()
          ctx.font = rightIcon.font
          ctx.fillStyle = rightIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(rightIcon.text, rightIcon.x, rightIcon.y)
          ctx.restore()
        }
      }
    },
    chartAreaBorderPlugin() {
      return {
        id: 'chartAreaBorder',
        beforeDraw(chart, args, options) {
          const {ctx, chartArea: {left, top, width, height}} = chart

          ctx.save();
          ctx.strokeStyle = options.borderColor || 'black'
          ctx.lineWidth = options.borderWidth || 2

          if (chart.options.indexAxis === 'y') {
            const dataLength = chart?.data?.datasets[0]?.data.length || 1
            const fixedHeight = height / dataLength / 6

            ctx.beginPath();
            ctx.moveTo(left, top + fixedHeight)
            ctx.lineTo(left, top + height - fixedHeight)
            ctx.stroke()

            ctx.beginPath()
            ctx.moveTo(left, top + height - fixedHeight)
            ctx.lineTo(left + width, top + height - fixedHeight)
            ctx.stroke();
          } else {
            const extension = 15
            const padding = 1

            ctx.beginPath();
            ctx.moveTo(left, top - extension)
            ctx.lineTo(left, top + height + padding)
            ctx.stroke()

            ctx.beginPath()
            ctx.moveTo(left, top + height + padding)
            ctx.lineTo(left + width + extension, top + height + padding)
            ctx.stroke();
          }

          ctx.restore()
        }
      }
    },
    customTitlePlugin() {
      return {
        id: 'customTitle',
        afterDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, width } } = chart

          if (options?.y?.display) {
            ctx.save()
            ctx.fillStyle = options.y.color || 'black'

            const font = options.y.font || {}
            const fontSize = font.size || 10;
            const fontFamily = font.family || 'sans-serif'
            const fontWeight = font.weight || 'normal'
            ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`

            ctx.textAlign = 'center'
            ctx.textBaseline = 'top'

            const xPos = left / 2
            const yPos = top - 25

            ctx.fillText(options.y.text, xPos, yPos)
            ctx.restore()
          }
        }
      }
    },
    baselinePlugin() {
      return {
        id: 'baselinePlugin',
        afterDatasetsDraw(chart) {
          const { ctx, scales, chartArea } = chart;
          const xScale = scales.x
          const yScale = scales.y
          const meta = chart.getDatasetMeta(0)
          const data = chart.data.datasets[0].data
          const padding = 5

          ctx.save()
          ctx.lineWidth = 1
          ctx.strokeStyle = '#FF0000'

          meta.data.forEach((bar, i) => {
            const model = bar
            const barHeight = model.height
            const y = model.y

            const baselineX = xScale.getPixelForValue(data[i]?.outlook)

            const barTop = y - barHeight / 2
            const barBottom = y + barHeight / 2

            ctx.beginPath()
            ctx.moveTo(baselineX, barTop - padding)
            ctx.lineTo(baselineX, barBottom + padding)
            ctx.stroke()
          })

          ctx.restore()
        }
      }
    },
    gradientFillPlugin() {
      return {
        id: 'gradientFillPlugin',
        afterDatasetDraw(chart) {
          const { ctx } = chart
          chart.data.datasets.forEach((dataset, i) => {
            const meta = chart.getDatasetMeta(i)
            meta.data.forEach((bar) => {
              const { x, y, base, height } = bar
              const width = x - base
              const barHeight = height;

              const gradient = ctx.createLinearGradient(base, y, x, y)
              gradient.addColorStop(0, '#428AF5')
              gradient.addColorStop(1, '#9BD8F7')

              const radius = 5
              const top = y - barHeight / 2
              const bottom = y + barHeight / 2

              ctx.beginPath()
              ctx.moveTo(base + radius, top)
              ctx.lineTo(base + width - radius, top)
              ctx.quadraticCurveTo(base + width, top, base + width, top + radius)
              ctx.lineTo(base + width, bottom - radius)
              ctx.quadraticCurveTo(base + width, bottom, base + width - radius, bottom)
              ctx.lineTo(base + radius, bottom)
              ctx.quadraticCurveTo(base, bottom, base, bottom - radius)
              ctx.lineTo(base, top + radius)
              ctx.quadraticCurveTo(base, top, base + radius, top)
              ctx.closePath()

              ctx.fillStyle = gradient
              ctx.fill();
            })
          })

          return false
        }
      }
    },
    customFillPlugin() {
      return {
        id: 'customFill',
        beforeDraw(chart, args, options) {
          const { ctx, chartArea: { left, right, top, bottom, height }, scales: { x, y } } = chart
          const meta = chart.getDatasetMeta(0)
          const points = meta.data
          let firstIndex = options?.nextMonth
          if(firstIndex === 12) firstIndex = 1
          const pointDistance = ((right - left) / (meta.data.length - 1)) / 2
          const yellowFillStartX = points[firstIndex].x - pointDistance

          const padding = isNumber(chart?.options?.layout?.padding)
            ? chart?.options?.layout?.padding
            : 0

          ctx.fillStyle = '#FDF4BA'
          ctx.fillRect(
            yellowFillStartX,
            0,
            x.right - yellowFillStartX,
            height + padding
          )
        }
      }
    },
    roundedTickPlugin() {
      return {
        id: 'roundedTicks',
        afterDraw(chart) {
          const { ctx, chartArea, scales } = chart;
          const xScale = scales.x;
          const yStart = chartArea.bottom;

          ctx.save();
          ctx.fillStyle = 'rgba(0, 0, 0, 1)';

          xScale.ticks.forEach((tick, i) => {
            const x = xScale.getPixelForTick(i);
            ctx.beginPath();
            ctx.arc(x, yStart + 10, 3, 0, Math.PI * 2); // y + 5 to go below the axis line
            ctx.fill();
          });

          ctx.restore();
        }
      }
    },
    drawBarLabelPlugin() {
      return {
        id: 'drawBarLabel',
        afterDatasetDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart
          const meta = chart.getDatasetMeta(0)
          const padding = 10

          meta.data.forEach((arc, index) => {
            const value = data.datasets[0].data[index].x
            const {x, y, width, height, base} = arc

            ctx.save()
            ctx.fillStyle = '#000'
            ctx.font = '800 14px Poppins'
            ctx.textAlign = 'left'
            ctx.textBaseline = 'middle'
            ctx.fillText(value, base + padding, y)
            ctx.restore()
          });
        }
      }
    },
    drawBarValuePlugin() {
      return {
        id: 'valueLabel',
        afterDatasetsDraw: chart => {
          const ctx = chart.ctx;
          ctx.save();
          chart.data.datasets.forEach((dataset, dsIdx) => {
            const meta = chart.getDatasetMeta(dsIdx);
            meta.data.forEach((bar, i) => {
              const value = dataset.data[i];
              const barWidth = bar.x - bar.base;
              ctx.font = 'semibold 16px Poppins';
              
              const text = value.toString();
              const textWidth = ctx.measureText(text).width;
              let color, align;
              if (barWidth > textWidth + 16) {
                color = '#000';
                align = 'center';
              } else {
                color = '#222';
                align = 'center';
              }
              ctx.fillStyle = color;
              ctx.textAlign = align;
              ctx.textBaseline = 'middle';
              if (dataset.type !== 'line' && value > 0)
                ctx.fillText(text, bar.x, bar.y - 12);
            });
          });
          ctx.restore();
        }
      };
    },
    slice(array, start, end) {
      return array.slice(start, end)
    },
    isNumber(value) {
      return typeof value === 'number' && !isNaN(value)
    }
  }
};
</script>

<style lang="scss" scoped>

</style> 